/**
 * FASE G — Dashboard KPI cross-ramo.
 *
 * Widget prominenti: Tasso conversione · Tempo medio chiusura · Top compagnie
 * Widget secondari: totali MTD/YTD · clienti ricorrenti · task aperti
 * Filtri: periodo (MTD / YTD / ultimo mese / ultimo trimestre / custom) + ramo
 * Refresh: manuale + auto-refresh 60s opt-in
 *
 * Endpoint singolo `/api/admin/kpi/summary` con cache backend 60s.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  TrendingUp, TrendingDown, RefreshCw, ArrowLeft, Building2,
  Clock, Percent, Users, ListChecks, ArrowRight, Calendar, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import LeadPerConvenzioneWidget from "@/components/admin/LeadPerConvenzioneWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { key: "mtd",          label: "MTD (mese corrente)" },
  { key: "ytd",          label: "YTD (anno corrente)" },
  { key: "last_month",   label: "Ultimo mese" },
  { key: "last_quarter", label: "Ultimo trimestre" },
  { key: "custom",       label: "Custom…" },
];
const RAMI = [
  { key: "all",              label: "Tutti i rami" },
  { key: "d_o",              label: "D&O" },
  { key: "cyber",            label: "Cyber" },
  { key: "fine_arts",        label: "Fine Arts" },
  { key: "convenzioni_auto", label: "RCA" },
];

const RAMO_COLOR = {
  d_o:              "#4F46E5",
  cyber:            "#9333EA",
  fine_arts:        "#DB2777",
  convenzioni_auto: "#059669",
};

function DeltaBadge({ delta }) {
  if (delta === 0 || delta == null) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[11px] font-medium
                        text-slate-500">
        <span>=</span>0.0%
      </span>
    );
  }
  const positive = delta > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-medium
                        ${positive ? "text-emerald-600" : "text-red-600"}`}>
      {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {positive ? "+" : ""}{delta.toFixed(1)}%
    </span>
  );
}

function KpiCard({ title, icon: Icon, value, unit = "", delta = null,
                    subtitle = null, testid, size = "md", accent = "#163321" }) {
  const isLg = size === "lg";
  return (
    <div className={`bg-white border border-[#E5E2DB] rounded-2xl p-5 shadow-sm
                       flex flex-col ${isLg ? "min-h-[180px]" : "min-h-[120px]"}`}
          data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <div className="text-xs uppercase text-slate-500 font-semibold tracking-wide">
          {title}
        </div>
        <div className="p-1.5 rounded-lg" style={{ background: `${accent}15` }}>
          <Icon className="w-4 h-4" style={{ color: accent }} />
        </div>
      </div>
      <div className={`font-heading text-[#0F1F14] leading-none
                        ${isLg ? "text-4xl" : "text-2xl"}`}>
        {value}
        {unit && <span className="text-base ml-1 text-slate-500">{unit}</span>}
      </div>
      {delta !== null && (
        <div className="mt-2"><DeltaBadge delta={delta} /></div>
      )}
      {subtitle && (
        <div className="mt-auto pt-2 text-[11px] text-slate-500">{subtitle}</div>
      )}
    </div>
  );
}


function RamoBreakdownBars({ breakdown, valueKey = "rate", unit = "%" }) {
  const entries = Object.entries(breakdown || {});
  const max = Math.max(1, ...entries.map(([, v]) => Number(v[valueKey] || 0)));
  if (entries.length === 0) return (
    <div className="text-xs text-slate-400 italic">Nessun dato nel periodo.</div>
  );
  return (
    <div className="space-y-1.5">
      {entries.map(([ramo, v]) => {
        const val = Number(v[valueKey] || 0);
        return (
          <div key={ramo} className="flex items-center gap-2">
            <div className="w-16 text-[11px] text-slate-700">{v.label || ramo}</div>
            <div className="flex-1 bg-slate-100 rounded-full h-2 relative">
              <div className="absolute inset-y-0 left-0 rounded-full"
                    style={{ width: `${(val / max) * 100}%`,
                              background: RAMO_COLOR[ramo] || "#163321" }} />
            </div>
            <div className="w-12 text-right text-[11px] font-medium tabular-nums">
              {val.toFixed(1)}{unit}
            </div>
          </div>
        );
      })}
    </div>
  );
}


function TopCompaniesChart({ items, onlyWon, onToggleWon }) {
  const max = Math.max(1, ...items.map((i) => i.count));
  return (
    <div className="bg-white border border-[#E5E2DB] rounded-2xl p-5"
          data-testid="kpi-top-companies">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs uppercase text-slate-500 font-semibold tracking-wide">
            Top 5 compagnie
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            per numero richieste nel periodo
          </div>
        </div>
        <div className="p-1.5 rounded-lg" style={{ background: "#C9A44815" }}>
          <Building2 className="w-4 h-4" style={{ color: "#C9A448" }} />
        </div>
      </div>
      <label className="inline-flex items-center gap-2 mb-3 text-[11px] cursor-pointer"
              data-testid="kpi-only-won-toggle">
        <input type="checkbox" checked={onlyWon} onChange={onToggleWon}
                className="w-3.5 h-3.5 accent-[#163321]" />
        <span className="text-slate-600">Solo chiuse positive</span>
      </label>
      {items.length === 0 && (
        <div className="text-xs text-slate-400 italic py-4">
          Nessuna compagnia nel periodo.
        </div>
      )}
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={it.company + i}
                data-testid={`kpi-top-company-row-${i}`}
                className="flex items-center gap-3">
            <div className="w-32 text-xs text-slate-700 truncate" title={it.company}>
              {it.company}
            </div>
            <div className="flex-1 bg-slate-100 rounded h-6 relative">
              <div className="absolute inset-y-0 left-0 bg-[#163321] rounded"
                    style={{ width: `${(it.count / max) * 100}%` }} />
              <span className="absolute inset-0 flex items-center justify-end pr-2
                                text-[10px] font-medium text-white">
                {it.count}
              </span>
            </div>
            {it.won_count > 0 && (
              <div className="w-16 text-right text-[10px] text-emerald-600">
                ✓ {it.won_count}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


export default function AdminKpiPage() {
  const [period, setPeriod] = useState("mtd");
  const [ramo, setRamo] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [onlyWon, setOnlyWon] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ period, ramo });
      if (period === "custom" && customFrom && customTo) {
        params.set("custom_from", customFrom);
        params.set("custom_to", customTo);
      }
      if (onlyWon) params.set("only_won", "true");
      const r = await axios.get(`${API}/admin/kpi/summary?${params}`,
                                    { withCredentials: true });
      setData(r.data);
    } catch (e) {
      console.error(e);
      setError("Errore caricamento KPI");
      toast.error("Errore caricamento KPI");
    } finally {
      setLoading(false);
    }
  }, [period, ramo, customFrom, customTo, onlyWon]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [autoRefresh, load]);

  const conv = data?.conversion || {};
  const closure = data?.closure_time_days || {};
  const top = data?.top_companies || [];
  const sec = data?.secondary || {};

  return (
    <div className="p-6 max-w-full" data-testid="admin-kpi-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link to="/admin/dashboard"
                   className="text-sm text-slate-500 hover:text-[#0F1F14] inline-flex items-center gap-1"
                   data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </Link>
            <span className="text-slate-300">·</span>
            <Link to="/admin/cockpit"
                   className="text-sm text-slate-500 hover:text-[#0F1F14]">
              Richieste
            </Link>
          </div>
          <h1 className="font-heading text-3xl text-[#0F1F14]">
            KPI · Analytics cross-ramo
          </h1>
          <p className="text-sm text-[#4A5E51] mt-1">
            Conversione · tempo chiusura · top compagnie ·{" "}
            {data?.cache_hit && (
              <span className="text-[10px] uppercase font-medium text-slate-400">
                cache hit
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <input type="checkbox" checked={autoRefresh}
                    data-testid="autorefresh-toggle"
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    className="w-3.5 h-3.5 accent-[#163321]" />
            Auto-refresh 60s
          </label>
          <button onClick={load}
                   data-testid="kpi-refresh-btn"
                   className="p-2 rounded-lg hover:bg-slate-100">
            {loading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <RefreshCw className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Filtri */}
      <div className="bg-white border border-[#E5E2DB] rounded-2xl p-4 mb-5 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-[11px] uppercase text-slate-500 font-medium">Periodo</label>
          <select value={period}
                   onChange={(e) => setPeriod(e.target.value)}
                   data-testid="kpi-period-select"
                   className="mt-1 block px-3 py-2 text-sm border rounded-lg">
            {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </div>
        {period === "custom" && (
          <>
            <div>
              <label className="text-[11px] uppercase text-slate-500 font-medium">Da</label>
              <input type="date" value={customFrom}
                      data-testid="kpi-custom-from"
                      onChange={(e) => setCustomFrom(e.target.value)}
                      className="mt-1 block px-3 py-2 text-sm border rounded-lg" />
            </div>
            <div>
              <label className="text-[11px] uppercase text-slate-500 font-medium">A</label>
              <input type="date" value={customTo}
                      data-testid="kpi-custom-to"
                      onChange={(e) => setCustomTo(e.target.value)}
                      className="mt-1 block px-3 py-2 text-sm border rounded-lg" />
            </div>
          </>
        )}
        <div>
          <label className="text-[11px] uppercase text-slate-500 font-medium">Ramo</label>
          <select value={ramo}
                   onChange={(e) => setRamo(e.target.value)}
                   data-testid="kpi-ramo-select"
                   className="mt-1 block px-3 py-2 text-sm border rounded-lg">
            {RAMI.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {/* PRIMARY WIDGETS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <div className="bg-white border border-[#E5E2DB] rounded-2xl p-5 shadow-sm"
              data-testid="kpi-conversion-card">
          <div className="flex items-start justify-between mb-2">
            <div className="text-xs uppercase text-slate-500 font-semibold tracking-wide">
              Tasso di conversione
            </div>
            <div className="p-1.5 rounded-lg bg-emerald-100">
              <Percent className="w-4 h-4 text-emerald-700" />
            </div>
          </div>
          <div className="font-heading text-4xl text-[#0F1F14] leading-none">
            {(conv.current_rate ?? 0).toFixed(1)}
            <span className="text-base ml-1 text-slate-500">%</span>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <DeltaBadge delta={conv.delta_pct || 0} />
            <span className="text-[11px] text-slate-500">vs periodo precedente</span>
          </div>
          <div className="mt-3 pt-3 border-t border-[#E5E2DB]">
            <div className="text-[11px] text-slate-500 mb-2">
              {conv.won_current || 0} chiuse positive su {conv.total_current || 0} totali
            </div>
            <RamoBreakdownBars breakdown={conv.breakdown_by_ramo} valueKey="rate" unit="%" />
          </div>
        </div>

        <div className="bg-white border border-[#E5E2DB] rounded-2xl p-5 shadow-sm"
              data-testid="kpi-closure-card">
          <div className="flex items-start justify-between mb-2">
            <div className="text-xs uppercase text-slate-500 font-semibold tracking-wide">
              Tempo medio chiusura
            </div>
            <div className="p-1.5 rounded-lg bg-blue-100">
              <Clock className="w-4 h-4 text-blue-700" />
            </div>
          </div>
          <div className="font-heading text-4xl text-[#0F1F14] leading-none">
            {(closure.avg ?? 0).toFixed(1)}
            <span className="text-base ml-1 text-slate-500">giorni</span>
          </div>
          <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500">
            <span>Mediana <b className="text-slate-800">{(closure.median ?? 0).toFixed(1)}g</b></span>
            <span>·</span>
            <span>P90 <b className="text-slate-800">{(closure.p90 ?? 0).toFixed(1)}g</b></span>
          </div>
          <div className="mt-3 pt-3 border-t border-[#E5E2DB]">
            <div className="text-[11px] text-slate-500 mb-2">
              su {closure.count || 0} pratiche chiuse
            </div>
            <RamoBreakdownBars breakdown={closure.breakdown_by_ramo}
                                 valueKey="avg" unit="g" />
          </div>
        </div>

        <TopCompaniesChart items={top} onlyWon={onlyWon}
                             onToggleWon={() => setOnlyWon(!onlyWon)} />
      </div>

      {/* SECONDARY WIDGETS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          title="Richieste nel periodo"
          icon={ArrowRight}
          value={sec.total_current || 0}
          delta={sec.total_delta_pct}
          subtitle={`Precedente: ${sec.total_previous || 0}`}
          testid="kpi-secondary-total" accent="#163321"
        />
        <KpiCard
          title="Clienti ricorrenti"
          icon={Users}
          value={sec.clienti_ricorrenti || 0}
          subtitle="≥3 richieste, mai chiuso positivo"
          testid="kpi-secondary-recurring" accent="#B91C1C"
        />
        <KpiCard
          title="Task follow-up aperti"
          icon={ListChecks}
          value={sec.tasks_aperti || 0}
          subtitle={
            <Link to="/admin/cockpit?open_tasks=1"
                   className="underline hover:text-[#0F1F14]">
              Vedi lista →
            </Link>
          }
          testid="kpi-secondary-tasks" accent="#F59E0B"
        />
        <KpiCard
          title="Funnel drop-off wizard"
          icon={Calendar}
          value="—"
          subtitle="Feature futura · richiede tracking step-level"
          testid="kpi-secondary-funnel" accent="#6B7280"
        />
      </div>

      {/* ITER 69 — Widget Lead per Convenzione (matrice partner × ramo) */}
      <div className="mt-6" data-testid="kpi-lead-per-convenzione-section">
        <LeadPerConvenzioneWidget />
      </div>
    </div>
  );
}
